import { dash, esc, lighten, resolveLogoDataUri, waitForPopupImages, waitForPopupReady } from './print-shared';

import type { ClinicBranding } from './useClinicIdentity';

type Translate = (key: string) => string;

interface AnamnesisShape {
  chief_complaint: string | null;
  symptom_onset_days: number | null;
  previous_illnesses: string | null;
  current_medications: string | null;
  vaccination_status: string | null;
  deworming_status: string | null;
  diet_notes: string | null;
  environment_notes: string | null;
  other_notes: string | null;
}

interface PhysicalExamShape {
  body_weight_grams: number | null;
  body_temperature_celsius_tenths: number | null;
  heart_rate_bpm: number | null;
  respiratory_rate_bpm: number | null;
  mucous_membranes: string | null;
  hydration_status: string | null;
  consciousness_level: string | null;
  body_condition_score: number | null;
  general_impression: string | null;
  musculoskeletal_notes: string | null;
  cardiovascular_notes: string | null;
  respiratory_notes: string | null;
  gastrointestinal_notes: string | null;
  genitourinary_notes: string | null;
  neurological_notes: string | null;
  integumentary_notes: string | null;
  lymph_node_notes: string | null;
  abdomen_notes: string | null;
}

interface DiagnosisShape {
  is_primary: boolean;
  diagnosis_kind: string;
  description: string;
  prognosis: string | null;
  notes: string | null;
}

interface PlanTreatmentShape {
  treatment_notes: string | null;
  medications_notes: string | null;
  diet_recommendations: string | null;
  activity_restrictions: string | null;
  follow_up_notes: string | null;
  owner_instructions: string | null;
}

interface AddendumShape {
  content: string;
  created_at: string;
}

export interface ClinicalRecordPrintContext {
  pet_record_code: string | null;
  patient_name: string;
  species: string | null;
  breed: string | null;
  color: string | null;
  sex: string | null;
  age_label: string | null;
  weight_label: string | null;
  owner_name: string;
  owner_phone: string | null;
  visit_date: string;
  chief_complaint: string | null;
  anamnesis: AnamnesisShape | null;
  physical_exam: PhysicalExamShape | null;
  diagnoses: DiagnosisShape[];
  plan_treatment: PlanTreatmentShape | null;
  addendums: AddendumShape[];
  vet_name: string;
}

function row(label: string, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  return `<div class="field"><span class="lbl">${esc(label)}:</span> ${esc(value)}</div>`;
}

function block(title: string, value: string | null | undefined): string {
  if (!value) return '';
  return `<div class="block"><h4>${esc(title)}</h4><p>${esc(value)}</p></div>`;
}

/**
 * Opens a print-ready, 2-page clinical record (expediente clínico) document and triggers
 * the browser print dialog. Same mechanism as printPrescription — no external PDF dependency.
 * Returns false if the popup was blocked so the caller can surface a message.
 */
export async function printClinicalRecord(
  branding: ClinicBranding,
  ctx: ClinicalRecordPrintContext,
  t: Translate,
): Promise<boolean> {
  const logo_data_uri = await resolveLogoDataUri(branding.logo_url);
  const brand = esc(branding.brand_color_hex);
  const box_bg = esc(lighten(branding.brand_color_hex, 0.9));
  const box_border = esc(lighten(branding.brand_color_hex, 0.7));
  const logo_markup = logo_data_uri
    ? `<img class="logo" src="${esc(logo_data_uri)}" alt="${esc(branding.clinic_name)}" />`
    : '';

  const exam = ctx.physical_exam;
  const efg_fields = [
    row(t('clinical.f_body_weight_grams'), ctx.weight_label ?? exam?.body_weight_grams ?? null),
    row(
      t('clinical.f_body_temperature_celsius_tenths'),
      exam?.body_temperature_celsius_tenths != null
        ? `${(exam.body_temperature_celsius_tenths / 10).toFixed(1)} °C`
        : null,
    ),
    row('FC', exam?.heart_rate_bpm != null ? `${exam.heart_rate_bpm} bpm` : null),
    row('FR', exam?.respiratory_rate_bpm != null ? `${exam.respiratory_rate_bpm} rpm` : null),
    row('Mucosas', exam?.mucous_membranes ?? null),
    row('Hidratación', exam?.hydration_status ?? null),
    row('Estado de conciencia', exam?.consciousness_level ?? null),
    row('CC', exam?.body_condition_score ?? null),
  ]
    .filter(Boolean)
    .join('');

  const efg_notes = [
    block('Impresión general', exam?.general_impression ?? null),
    block('Musculoesquelético', exam?.musculoskeletal_notes ?? null),
    block('Cardiovascular', exam?.cardiovascular_notes ?? null),
    block('Respiratorio', exam?.respiratory_notes ?? null),
    block('Gastrointestinal', exam?.gastrointestinal_notes ?? null),
    block('Genitourinario', exam?.genitourinary_notes ?? null),
    block('Neurológico', exam?.neurological_notes ?? null),
    block('Piel y anexos', exam?.integumentary_notes ?? null),
    block('Ganglios linfáticos', exam?.lymph_node_notes ?? null),
    block('Abdomen', exam?.abdomen_notes ?? null),
  ]
    .filter(Boolean)
    .join('');

  const anamnesis_html = ctx.anamnesis
    ? [
        block(t('clinical.f_previous_illnesses'), ctx.anamnesis.previous_illnesses),
        block(t('clinical.f_current_medications'), ctx.anamnesis.current_medications),
        block('Vacunación', ctx.anamnesis.vaccination_status),
        block('Desparasitación', ctx.anamnesis.deworming_status),
        block('Dieta', ctx.anamnesis.diet_notes),
        block('Ambiente', ctx.anamnesis.environment_notes),
        block(t('clinical.f_other_notes'), ctx.anamnesis.other_notes),
      ]
        .filter(Boolean)
        .join('')
    : '';

  const diagnoses_html =
    ctx.diagnoses.length === 0
      ? `<p class="empty">${esc(t('clinical.no_diagnoses'))}</p>`
      : `<ul class="dx-list">${ctx.diagnoses
          .map(
            (d) => `<li>
              ${d.is_primary ? '<span class="star">★</span> ' : ''}
              <strong>${esc(t(`clinical.diagnosis_kind_${d.diagnosis_kind}`))}</strong>: ${esc(d.description)}
              ${d.prognosis ? `<div class="muted">Pronóstico: ${esc(d.prognosis)}</div>` : ''}
              ${d.notes ? `<div class="muted">${esc(d.notes)}</div>` : ''}
            </li>`,
          )
          .join('')}</ul>`;

  const plan_html = ctx.plan_treatment
    ? [
        block(t('clinical.f_treatment_notes'), ctx.plan_treatment.treatment_notes),
        block(t('clinical.f_medications_notes'), ctx.plan_treatment.medications_notes),
        block('Recomendaciones de dieta', ctx.plan_treatment.diet_recommendations),
        block('Restricción de actividad', ctx.plan_treatment.activity_restrictions),
        block(t('clinical.f_follow_up_notes'), ctx.plan_treatment.follow_up_notes),
        block('Indicaciones al propietario', ctx.plan_treatment.owner_instructions),
      ]
        .filter(Boolean)
        .join('')
    : `<p class="empty">—</p>`;

  const addendums_html =
    ctx.addendums.length === 0
      ? `<p class="empty">${esc(t('clinical.no_addendums'))}</p>`
      : `<ul class="addendum-list">${ctx.addendums
          .map(
            (a) => `<li><span class="muted">${esc(new Date(a.created_at).toLocaleString())}</span><p>${esc(a.content)}</p></li>`,
          )
          .join('')}</ul>`;

  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${esc(t('clinical.record_doc_title'))} — ${esc(ctx.patient_name)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #111; margin: 20mm 16mm; }
  .page { }
  .page + .page { page-break-before: always; }
  .logo-wrap { text-align: center; margin-bottom: 4px; }
  .logo { max-width: 200px; max-height: 80px; }
  .center-title { text-align: center; }
  .center-title .brand { font-size: 18px; font-weight: 700; color: ${brand}; }
  .center-title .doc-title { font-size: 15px; font-weight: 600; color: #444; margin-top: 2px; }
  .center-title .record-code { font-size: 12px; color: #666; margin-top: 2px; }
  header { border-bottom: 2px solid ${brand}; padding-bottom: 10px; margin-bottom: 12px; }
  .tutor-row { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 10px; }
  h3.section-title { font-size: 13px; font-weight: 700; color: ${brand}; margin: 14px 0 6px; border-bottom: 1px solid ${box_border}; padding-bottom: 3px; }
  .box-title { background: ${box_bg}; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 16px; font-size: 12px; }
  .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 12px; font-size: 12px; margin-bottom: 6px; }
  .field .lbl { color: #666; }
  .anamnesis-box, .block-wrap { border: 1px solid ${box_border}; border-radius: 6px; padding: 6px 10px; }
  .block { margin-bottom: 6px; font-size: 12px; }
  .block:last-child { margin-bottom: 0; }
  .block h4 { margin: 0 0 2px; font-size: 11px; font-weight: 700; color: #444; }
  .block p { margin: 0; }
  .empty { color: #777; font-size: 12px; }
  .dx-list, .addendum-list { list-style: none; padding: 0; margin: 0; font-size: 12px; }
  .dx-list li, .addendum-list li { border: 1px solid #ddd; border-radius: 6px; padding: 6px 8px; margin-bottom: 6px; }
  .star { color: #b8860b; }
  .muted { color: #666; font-size: 11px; }
  footer.vet { margin-top: 20px; padding-top: 8px; border-top: 1px solid #ccc; font-size: 12px; text-align: center; }
  @media print { body { margin: 0; padding: 16mm 14mm; } @page { margin: 0; } }
</style>
</head>
<body>
  <section class="page">
    <header>
      ${logo_markup ? `<div class="logo-wrap">${logo_markup}</div>` : ''}
      <div class="center-title">
        <div class="brand">${esc(branding.clinic_name)}</div>
        <div class="doc-title">${esc(t('clinical.record_doc_title'))}</div>
        ${ctx.pet_record_code ? `<div class="record-code">No. Expediente: ${esc(ctx.pet_record_code)}</div>` : ''}
      </div>
    </header>
    <div class="tutor-row">
      <span><span class="field lbl">${esc(t('clinical.record_doc_tutor'))}:</span> ${esc(ctx.owner_name)}</span>
      <span><span class="field lbl">${esc(t('clinical.record_doc_date'))}:</span> ${esc(ctx.visit_date)}</span>
    </div>

    <h3 class="section-title">${esc(t('clinical.record_doc_reseña_title'))}</h3>
    <div class="grid-2">
      ${row('Paciente', ctx.patient_name)}
      ${row('Especie', ctx.species)}
      ${row('Raza', ctx.breed)}
      ${row('Color', ctx.color)}
      ${row('Sexo', ctx.sex)}
      ${row('Edad', ctx.age_label)}
      ${row('Peso', ctx.weight_label)}
      ${row('Teléfono', ctx.owner_phone)}
    </div>

    <h3 class="section-title">${esc(t('clinical.tab_anamnesis'))}</h3>
    <div class="block-wrap">
      ${row(t('clinical.f_chief_complaint'), ctx.chief_complaint) || `<p class="empty">—</p>`}
      ${anamnesis_html || ''}
    </div>

    <h3 class="section-title">${esc(t('clinical.record_doc_efg_title'))}</h3>
    <div class="grid-4">${efg_fields}</div>
    <div class="block-wrap anamnesis-box">${efg_notes || `<p class="empty">—</p>`}</div>
  </section>

  <section class="page">
    <h3 class="section-title">${esc(t('clinical.tab_diagnoses'))}</h3>
    ${diagnoses_html}

    <h3 class="section-title">${esc(t('clinical.tab_plan'))}</h3>
    <div class="block-wrap">${plan_html}</div>

    <h3 class="section-title">${esc(t('clinical.tab_addendums'))}</h3>
    ${addendums_html}

    <footer class="vet">${esc(t('clinical.record_doc_vet_in_charge'))}: ${dash(ctx.vet_name)}</footer>
  </section>
</body>
</html>`;

  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return false;
  w.opener = null;
  w.document.write(html);
  w.document.close();
  await waitForPopupReady(w);
  await waitForPopupImages(w);
  w.print();
  return true;
}
